import * as _ from "lodash";
import {
	IChartConfig,
	IDataEntries,
	BarOrientation,
	DataSortOrder,
	IXYChartConfig,
	ISeriesConfig,
	IValueStructure,
} from "./XYChart.d";
import { merge, get as lodashGet } from "lodash";

export function xyChartTransformation(
	xyChartData: any,
	config: IXYChartConfig,
	isHourGranularity: boolean,
	componentHeight: string,
	selectedNode?: string
): IChartConfig {
	console.log("CONFIG", config);
	const barOrientation = lodashGet(
		config,
		"barOrientation",
		BarOrientation.HORIZONTAL
	);

	const supportDownload: boolean = lodashGet(
		config,
		"supportDownload",
		false
	);
	const defaultConfig =
		barOrientation === BarOrientation.HORIZONTAL
			? defaultHorizontalConfig
			: defaultVerticalConfig;

	const chartConfig: IXYChartConfig = merge({}, defaultConfig, config);

	const dataConfig = chartConfig.data;

	const chartData = lodashGet(
		xyChartData,
		dataConfig!.dataPath!,
		xyChartData
	);

	const categoryAxisConfig = lodashGet(
		chartConfig,
		["axisParameters", "categoryAxis"],
		{}
	);
	const valueAxisConfig = lodashGet(
		chartConfig,
		["axisParameters", "valueAxis"],
		{}
	);

	const seriesConfigArray = lodashGet(chartConfig, "series", []);
	const legend = lodashGet(chartConfig, "legend", null!);
	const cursor = lodashGet(chartConfig, "cursor", null!);
	const sortProperties = lodashGet(dataConfig, "sort", null);

	let modifiedChartData = [];
	let tooltipHTMLArray: Array<string> = [];

	if (chartData) {
		modifiedChartData = dataProcessing(
			chartData,
			dataConfig,
			barOrientation
		) as any;
		tooltipHTMLArray = getTootltipHTMLArray(chartConfig, selectedNode);
	}

	if (sortProperties && modifiedChartData) {
		const sortKey = sortProperties.key || "sortIndex";
		const sortOrder = sortProperties.order;

		modifiedChartData.sort((a, b) => {
			if (
				Number.parseFloat(a[sortKey]) &&
				Number.parseFloat(b[sortKey])
			) {
				return sortOrder === DataSortOrder.ASCENDING
					? a[sortKey] - b[sortKey]
					: b[sortKey] - a[sortKey];
			} else {
				if (sortOrder === DataSortOrder.ASCENDING) {
					return a[sortKey] < b[sortKey] ? -1 : 1;
				} else {
					return a[sortKey] < b[sortKey] ? -1 : 1;
				}
			}
		});
	}

	const shouldRotateLabels = !isHourGranularity;
	// todo think of more explict use case
	if (!isHourGranularity && categoryAxisConfig.label === "PRODUCTION HOURS") {
		categoryAxisConfig.label = "";
	}

	const horizontalScrollThreshold = lodashGet(
		config,
		"horizontalScrollThreshold",
		0
	);

	return {
		modifiedData: modifiedChartData,
		barOrientation,
		tooltipHTMLArray,
		categoryAxisConfig,
		valueAxisConfig,
		seriesConfigArray,
		themes: chartConfig.themes,
		dataEntries: lodashGet(dataConfig, "dataEntries", []),
		legend,
		cursor,
		shouldRotateLabels,
		componentHeight,
		horizontalScrollThreshold,
		supportDownload,
	};
}

function dataProcessing(
	chartData: any,
	dataConfig: any,
	barOrientation: BarOrientation
) {
	const dataEntries: Array<IDataEntries> = lodashGet(
		dataConfig,
		"dataEntries",
		null
	);
	if (dataEntries == null) {
		return [];
	}

	if (!Array.isArray(chartData)) {
		return [];
	}

	return chartData.map((dataElement, index) => {
		let xAxisData;
		let yAxisData;
		let labelData: any;
		let hAxisDataKey;
		let vAxisDataKey;
		let labelDataKey: any;
		let newDataElement = dataElement;

		dataEntries.forEach((configElement, index) => {
			let categoryData: any;
			let valueData: any;

			const categoryAxisParameters = lodashGet(
				dataConfig,
				["parameters", index, "categoryAxis"],
				null
			);
			const valueAxisParameters = lodashGet(
				dataConfig,
				["parameters", index, "valueAxis"],
				null
			);

			hAxisDataKey = configElement.horizontalAxisDataKey;
			vAxisDataKey = configElement.verticalAxisDataKey;
			labelDataKey = configElement.labelDataKey;

			if (configElement.hasOwnProperty("categoryAxisValue")) {
				categoryData = configElement.categoryAxisValue;
			} else if (configElement.hasOwnProperty("categoryAxisProp")) {
				categoryData = lodashGet(
					dataElement,
					configElement.categoryAxisProp!
				);
			}

			if (configElement.hasOwnProperty("valueAxisValue")) {
				valueData = configElement.valueAxisValue;
			} else if (configElement.hasOwnProperty("valueAxisProp")) {
				valueData = lodashGet(
					dataElement,
					configElement.valueAxisProp!
				);
			}

			const [xValue, yValue, xAxisDataParameters, yAxisDataParameters] =
				barOrientation === "HORIZONTAL"
					? [
							valueData,
							categoryData,
							valueAxisParameters,
							categoryAxisParameters,
					  ]
					: [
							categoryData,
							valueData,
							categoryAxisParameters,
							valueAxisParameters,
					  ];

			xAxisData = xValue;
			yAxisData = yValue;

			if (xAxisDataParameters) {
				const prefixSuffixData = getPrefixAndSuffixValues(
					xAxisDataParameters,
					chartData
				);
				xAxisData = `${prefixSuffixData.valuePrefix}${xValue}${prefixSuffixData.valueSuffix}`;
			}

			if (yAxisDataParameters) {
				const prefixSuffixData = getPrefixAndSuffixValues(
					yAxisDataParameters,
					chartData
				);
				yAxisData = `${prefixSuffixData.valuePrefix}${yValue}${prefixSuffixData.valueSuffix}`;
			}

			if (configElement.hasOwnProperty("labelValue")) {
				labelData = configElement.labelValue;
			} else if (configElement.hasOwnProperty("labelProp")) {
				labelData = lodashGet(dataElement, configElement.labelProp!);
			}

			const labelDataParameters = lodashGet(
				dataConfig,
				["parameters", "label"],
				null
			);
			if (labelDataParameters) {
				const prefixSuffixData = getPrefixAndSuffixValues(
					labelDataParameters,
					chartData
				);
				labelData = `${prefixSuffixData.valuePrefix}${labelData}${prefixSuffixData.valueSuffix}`;
			}

			newDataElement = {
				...newDataElement,
				[hAxisDataKey]: xAxisData,
				[vAxisDataKey]: yAxisData,
				[labelDataKey]: labelData,
			};
		});
		return { ...newDataElement, sortIndex: index + 1 };
	});
}

function getPrefixAndSuffixValues(element: any, dataObject: any) {
	let valuePrefix = "";
	let valueSuffix = "";

	if (element.hasOwnProperty("prefixValue")) {
		valuePrefix = element.prefixValue;
	} else {
		valuePrefix = lodashGet(dataObject, element.prefixProp, "");
	}

	if (element.hasOwnProperty("suffixValue")) {
		valueSuffix = element.suffixValue;
	} else {
		valueSuffix = lodashGet(dataObject, element.suffixProp, "");
	}

	return { valuePrefix, valueSuffix };
}

function getTootltipHTMLArray(
	config: IXYChartConfig,
	selectedNode?: string
): Array<string> {
	const seriesConfig: Array<ISeriesConfig> = lodashGet(config, "series", []);

	return seriesConfig.map((series) => {
		const toolTipArray = lodashGet(series, ["tooltip", "tooltipText"], []);
		const node = lodashGet(series, ["tooltip", "node"], null);
		let tooltipHTML = "";
		if (node) {
			tooltipHTML =
				" " + `<div class="${node.style}">${selectedNode}</div>`;
		}

		toolTipArray.forEach((element: IValueStructure) => {
			const styleClass = lodashGet(element, "style", "");
			const valueText = element.value;
			tooltipHTML +=
				" " + `<div class="${styleClass}">${valueText}</div>`;
		});
		return tooltipHTML;
	});
}

const defaultHorizontalConfig: IXYChartConfig = {
	themes: {
		categoryAxis: {
			labels: {
				template: {
					fill: "LABEL_GREY_1",
					fontSize: "TEXT_14",
				},
			},
		},
		valueAxis: {
			labels: {
				template: {
					fill: "LABEL_GREY_1",
					fontSize: "TEXT_14",
				},
			},
			titleFill: "LABEL_GREY_2",
		},
		series: [],
	},
	axisParameters: {
		categoryAxis: {
			cellStartLocation: 0.2,
			cellEndLocation: 0.8,
			autoGridCount: false,
			minGridDistance: 10,
			inversed: true,
			labels: {
				template: {
					minWidth: 160,
					maxWidth: 160,
					align: "right",
					truncate: true,
				},
			},
		},
		valueAxis: {
			maxPrecision: 0,
			labelFontSize: 10,
		},
	},
	series: [
		{
			name: "",
			columns: {
				template: {
					width: 50,
				},
			},
			labels: {
				horizontalCenter: "bottom",
				dx: 3,
			},
		},
	],
	data: {} as any
};

const defaultVerticalConfig: IXYChartConfig = {
	themes: {
		categoryAxis: {
			labels: {
				template: {
					fill: "LABEL_GREY_1",
					fontSize: "TEXT_14",
				},
			},
		},
		valueAxis: {
			labels: {
				template: {
					fill: "LABEL_GREY_1",
					fontSize: "TEXT_14",
				},
			},
			titleFill: "LABEL_GREY_2",
		},
		series: [],
	},
	axisParameters: {
		categoryAxis: {
			cellStartLocation: 0.2,
			cellEndLocation: 0.8,
			autoGridCount: false,
			inversed: true,
			labels: {
				template: {
					truncate: true,
					maxWidth: 100,
				},
			},
		},
		valueAxis: {
			maxPrecision: 0,
			labelFontSize: 10,
		},
	},
	series: [
		{
			name: "",
			columns: {
				template: {
					width: 70,
				},
			},
			labels: {
				verticalCenter: "bottom",
				dy: -3,
			},
		},
	],
	data: {} as any
};

/**
 * Sample Configs for Testing
 */

const testConfHorizontal = {
	themes: {
		categoryAxis: {
			labels: {
				template: {
					fill: "LABEL_GREY_1",
					fontSize: "TEXT_14",
				},
			},
		},
		valueAxis: {
			labels: {
				template: {
					fill: "LABEL_GREY_1",
					fontSize: "TEXT_14",
				},
			},
			titleFill: "LABEL_GREY_2",
		},
		series: [
			{
				columns: {
					template: {
						stroke: "BAR_BLUE",
						fill: "BAR_BLUE",
					},
				},
				labels: {
					fill: "LABEL_GREY_2",
					fontSize: "TEXT_14",
				},
			},
			{
				columns: {
					template: {
						stroke: "BAR_BLUE",
						fill: "LABEL_GREY_2",
					},
				},
				labels: {
					fill: "LABEL_GREY_1",
					fontSize: "TEXT_14",
				},
			},
		],
	},
	axisParameters: {
		categoryAxis: {
			dataKey: "yProp",
			cellStartLocation: 0.2,
			cellEndLocation: 0.8,
			autoGridCount: false,
			minGridDistance: 10,
			inversed: true,
			labels: {
				template: {
					minWidth: 160,
					maxWidth: 160,
					align: "right",
					truncate: true,
				},
			},
			gridCount: 500,
			grid: {
				template: {
					disabled: true,
					location: 0,
				},
			},
		},
		valueAxis: {
			maxPrecision: 0,
			labelFontSize: 10,
			label: "NUMBER OF DEFECTS",
			min: 0,
			extraMax: 0.02,
			grid: {
				disabled: false,
			},
		},
	},
	series: [
		{
			stacked: false,
			name: "Series 1",
			columns: {
				template: {
					width: 50,
				},
			},
			labels: {
				horizontalCenter: "left",
				dx: 3,
			},
			tooltip: {
				tooltipText: [
					{
						value: "{valueX} Defects",
						style: "zf-text-m",
					},
					{
						value: "{categoryY}",
						style: "zf-text-xs",
					},
				],
				pointerOrientation: "vertical",
				getFillFromObject: false,
				background: {
					fill: "BACKGROUND_WHITE",
				},
				label: {
					fill: "LABEL_BLACK",
					interactionsEnabled: true,
				},
			},
		},
		{
			stacked: false,
			name: "Series 2",
			columns: {
				template: {
					width: 50,
				},
			},
			labels: {
				horizontalCenter: "left",
				dx: 3,
			},
			tooltip: {
				tooltipText: [
					{
						value: "{valueX} Defects1",
						style: "zf-text-m",
					},
					{
						value: "{categoryY}",
						style: "zf-text-xs",
					},
				],
				pointerOrientation: "vertical",
				getFillFromObject: false,
				background: {
					fill: "BACKGROUND_WHITE",
				},
				label: {
					fill: "LABEL_BLACK",
					interactionsEnabled: true,
				},
			},
		},
	],
	legend: {
		position: "top",
		contentAlign: "left",
	},
	barOrientation: "HORIZONTAL",
	data: {
		dataPath: [0, "data"],
		dataEntries: [
			{
				valueAxisProp: "europe",
				categoryAxisProp: "year",
				labelProp: "",
				horizontalAxisDataKey: "xProp",
				verticalAxisDataKey: "yProp",
				labelDataKey: "label",
			},
			{
				valueAxisProp: "asia",
				categoryAxisProp: "year",
				labelProp: "",
				horizontalAxisDataKey: "xProp1",
				verticalAxisDataKey: "yProp1",
				labelDataKey: "label",
			},
		],
	},
};

const testConfVertical = {
	themes: {
		categoryAxis: {
			labels: {
				template: {
					fill: "LABEL_GREY_1",
					fontSize: "TEXT_14",
				},
			},
		},
		valueAxis: {
			labels: {
				template: {
					fill: "LABEL_GREY_1",
					fontSize: "TEXT_14",
				},
			},
			titleFill: "LABEL_GREY_2",
		},
		series: [
			{
				columns: {
					template: {
						stroke: "BAR_BLUE",
						fill: "BAR_BLUE",
					},
				},
				labels: {
					fill: "LABEL_GREY_2",
					fontSize: "TEXT_14",
				},
			},
			{
				columns: {
					template: {
						stroke: "BAR_BLUE",
						fill: "LABEL_GREY_2",
					},
				},
				labels: {
					fill: "LABEL_GREY_1",
					fontSize: "TEXT_14",
				},
			},
			{
				color: "red",
				strokeDasharray: "3,3",
				labels: {
					fill: "LABEL_GREY_2",
					fontSize: "TEXT_14",
				},
			},
		],
	},
	axisParameters: {
		categoryAxis: {
			dataKey: "yProp",
			gridCount: 500,
			minGridDistance: 40,
			grid: {
				template: {
					disabled: true,
					location: 0,
				},
			},
			labels: {
				template: {
					maxWidth: 100,
				},
			},
		},
		valueAxis: {
			min: 0,
			extraMax: 0.02,
			grid: {
				disabled: false,
			},
			labels: {
				template: {},
			},
		},
	},
	series: [
		{
			stacked: true,
			name: "Series 1",
			columns: {
				template: {
					width: 100,
				},
			},
			labels: {
				verticalCenter: "bottom",
				dy: -3,
			},
			tooltip: {
				tooltipText: [
					{
						value: "{categoryX} Defects",
						style: "zf-text-m",
					},
					{
						value: "{valueY}",
						style: "zf-text-xs",
					},
				],
				pointerOrientation: "vertical",
				getFillFromObject: false,
				background: {
					fill: "BACKGROUND_WHITE",
				},
				label: {
					fill: "LABEL_BLACK",
					interactionsEnabled: true,
				},
			},
		},
		{
			stacked: true,
			name: "Series 2",
			columns: {
				template: {
					width: 100,
				},
			},
			labels: {
				verticalCenter: "bottom",
				dy: -3,
			},
			tooltip: {
				tooltipText: [
					{
						value: "{categoryX} Defects1",
						style: "zf-text-m",
					},
					{
						value: "{valueY}",
						style: "zf-text-xs",
					},
				],
				pointerOrientation: "vertical",
				getFillFromObject: false,
				background: {
					fill: "BACKGROUND_WHITE",
				},
				label: {
					fill: "LABEL_BLACK",
					interactionsEnabled: true,
				},
			},
		},
		{
			stacked: true,
			chartType: "THRESHOLD",
			name: "Series 3",
			startLocation: 0.2,
			endLocation: 0.8,
			labels: {
				verticalCenter: "bottom",
				dy: -3,
			},
			tooltip: {
				tooltipText: [
					{
						value: "{categoryX} Threshold",
						style: "zf-text-m",
					},
					{
						value: "{valueY}",
						style: "zf-text-xs",
					},
				],
				pointerOrientation: "vertical",
				getFillFromObject: false,
				background: {
					fill: "BACKGROUND_WHITE",
				},
				label: {
					fill: "LABEL_BLACK",
					interactionsEnabled: true,
				},
			},
		},
	],
	legend: {
		position: "top",
		contentAlign: "left",
	},
	barOrientation: "VERTICAL",
	data: {
		dataPath: [0, "data"],
		dataEntries: [
			{
				valueAxisProp: "europe",
				categoryAxisProp: "year",
				labelProp: "year",
				horizontalAxisDataKey: "yProp",
				verticalAxisDataKey: "xProp",
				labelDataKey: "label",
			},
			{
				valueAxisProp: "asia",
				categoryAxisProp: "year",
				labelProp: "year",
				horizontalAxisDataKey: "yProp1",
				verticalAxisDataKey: "xProp1",
				labelDataKey: "label",
			},
			{
				valueAxisProp: "namerica",
				categoryAxisProp: "year",
				labelProp: "year",
				horizontalAxisDataKey: "yProp2",
				verticalAxisDataKey: "xProp2",
				labelDataKey: "label",
			},
		],
	},
};

const testConfLineSeries = {
	themes: {
		categoryAxis: {
			labels: {
				template: {
					fill: "LABEL_GREY_1",
					fontSize: "TEXT_14",
				},
			},
		},
		valueAxis: {
			labels: {
				template: {
					fill: "LABEL_GREY_1",
					fontSize: "TEXT_14",
				},
			},
			titleFill: "LABEL_GREY_2",
		},
		series: [
			{
				labels: {
					fill: "LABEL_GREY_2",
					fontSize: "TEXT_14",
				},
				bullet: {
					stroke: "BAR_BLUE",
					strokeWidth: 2,
					fill: "BAR_BLUE",
				},
				color: "BAR_BLUE",
			},
			{
				labels: {
					fill: "LABEL_GREY_1",
					fontSize: "TEXT_14",
				},
				color: "SOLID_BLUE",
				strokeDasharray: "3,3",
			},
		],
	},
	axisParameters: {
		categoryAxis: {
			dataKey: "yProp",
			gridCount: 500,
			minGridDistance: 40,
			labelFontSize: 10,
			label: "CATEGORY AXIS LABEL",
			grid: {
				template: {
					disabled: true,
					location: 0,
				},
			},
			labels: {
				template: {
					maxWidth: 100,
				},
			},
		},
		valueAxis: {
			min: 0,
			extraMax: 0.02,
			labelFontSize: 10,
			label: "VALUE AXIS LABEL",
			grid: {
				disabled: false,
			},
			labels: {
				template: {},
			},
		},
	},
	series: [
		{
			chartType: "LINE",
			name: "Actual Production",
			labels: {
				verticalCenter: "bottom",
				dy: -3,
			},
			tooltip: {
				tooltipText: [
					{
						value: "Year {categoryX}",
						style: "zf-text-m",
					},
					{
						value: "Actual : {valueY}",
						style: "zf-text-xs",
					},
					{
						value: "Planned : {asia}",
						style: "zf-text-xs",
					},
				],
				pointerOrientation: "vertical",
				getFillFromObject: false,
				background: {
					fill: "BACKGROUND_WHITE",
				},
				label: {
					fill: "LABEL_BLACK",
					interactionsEnabled: true,
				},
			},
		},
		{
			stacked: false,
			chartType: "THRESHOLD",
			name: "DHU THRESHOLD",
			startLocation: 0,
			endLocation: 1,
			labels: {
				verticalCenter: "bottom",
				dy: -3,
			},
		},
	],
	legend: {
		position: "top",
		contentAlign: "left",
	},
	barOrientation: "VERTICAL",
	data: {
		dataPath: [0, "data"],
		dataEntries: [
			{
				valueAxisProp: "europe",
				categoryAxisProp: "year",
				horizontalAxisDataKey: "yProp",
				verticalAxisDataKey: "xProp",
			},
			{
				valueAxisProp: "fix",
				categoryAxisProp: "year",
				horizontalAxisDataKey: "yProp1",
				verticalAxisDataKey: "xProp1",
			},
		],
	},
};

